import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowParams } from '@mui/x-data-grid';
import React, { useCallback, useMemo, useState } from 'react';

import { useFetchData } from '../../hooks/useFetchData';
import { UserRequestStatus, UserRequestType } from '../../Types/enums';
import { UserRequest } from '../../Types/userRequest';
import { userRequestsPathUrl } from '../../util/routesUrls';
import { UserRequestConfirmationDialog } from './UserRequestConfirmationDialog';

const typeLabels: Record<UserRequestType, string> = {
  [UserRequestType.DEEP_RESEARCH_CREDIT_REQUEST]: 'Credit Refill',
  [UserRequestType.APP_APPROVAL_REQUEST]: 'App Approval',
};

export const statusIconMap: Record<UserRequestStatus, JSX.Element> = {
  [UserRequestStatus.APPROVED]: <CheckCircleIcon color="success" />,
  [UserRequestStatus.DENIED]: <CancelIcon color="error" />,
  [UserRequestStatus.PENDING]: <PendingIcon sx={{ color: '#FFD700' }} />,
};

export const UserRequestPanel = (): JSX.Element => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<UserRequest | undefined>(undefined);
  const renderStatusIcon = (status: UserRequestStatus) => statusIconMap[status] || null;

  const { data: requests, refetch: refetchRequests } = useFetchData<{
    data: UserRequest[];
    total: number;
  }>({
    urlPath: userRequestsPathUrl(limit, page),
  });

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setSelectedRequest(undefined);
  }, [setOpenDialog, setSelectedRequest]);

  const handleRowClick = useCallback((params: GridRowParams) => {
    if (params.row.status !== UserRequestStatus.PENDING) return;
    setSelectedRequest(params.row as UserRequest);
    setOpenDialog(true);
  }, []);

  const handlePaginationChange = useCallback(
    (model: GridPaginationModel) => {
      const newPage = model.page + 1;
      const newLimit = model.pageSize;
      if (newLimit !== limit) {
        setPage(1);
        setLimit(newLimit);
      } else {
        setPage(newPage);
      }
    },
    [limit]
  );

  const formatShortDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(
      2,
      '0'
    )}`;
  };

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'requesterEmail',
        headerName: 'Email',
        flex: 1.25,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: 'type',
        headerName: 'Request Type',
        flex: 0.75,
        sortable: false,
        disableColumnMenu: true,
        valueFormatter: (params) => typeLabels[params.value as UserRequestType] || params.value,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.5,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ value }) => (
          <Box display="flex" justifyContent="center">
            {renderStatusIcon(value)}
          </Box>
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        flex: 0.5,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: ({ row }) => formatShortDate(row.createdAt),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        flex: 0.5,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: ({ row }) => formatShortDate(row.updatedAt),
      },
      {
        field: 'responderEmail',
        headerName: 'Reviewed By',
        flex: 1.25,
        sortable: false,
        disableColumnMenu: true,
      },
    ],
    []
  );

  return (
    <Box padding={3}>
      <Typography variant="h3" fontWeight={700} mb={3}>
        User Requests
      </Typography>

      <Box sx={{ width: '100%' }}>
        <DataGrid
          autoHeight
          rowCount={requests?.total || 0}
          rows={requests?.data || []}
          columns={columns}
          onPaginationModelChange={handlePaginationChange}
          paginationModel={{ page: page - 1, pageSize: limit }}
          pageSizeOptions={[5, 10, 20]}
          paginationMode="server"
          onRowClick={handleRowClick}
          sx={{
            cursor: 'pointer',
            '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            '& .MuiDataGrid-cell': {
              padding: '12px 10px',
            },
          }}
        />
      </Box>
      <UserRequestConfirmationDialog
        typeLabels={typeLabels}
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        selectedRequest={selectedRequest}
        refetchRequests={refetchRequests}
      />
    </Box>
  );
};
